import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Calendar,
  Phone,
  Mail,
  MapPin,
  CheckCircle,
  ArrowLeft,
  Home,
  Clock,
  Shield,
  Users
} from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BookHomeVisitPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const windowCount = location.state?.windowCount || 'Not specified';
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    postcode: '',
    address: '',
    preferredDate: '',
    preferredTime: '',
    notes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/calculator/home-visit-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          windowCount,
          source: 'landing_page'
        })
      });

      if (response.ok) {
        setIsSubmitted(true);
        toast({
          title: "Request Submitted!",
          description: "We'll be in touch shortly to confirm your home visit.",
        });
      } else {
        throw new Error('Failed to submit');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast({
        title: "Submission Error",
        description: "Please try again or call us directly.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const benefits = [
    { icon: Users, text: 'Meet our expert surveyor in person' },
    { icon: Clock, text: 'Typically 30-45 minute visit' },
    { icon: Shield, text: 'No obligation quote provided' },
    { icon: CheckCircle, text: 'Professional measurements taken' }
  ];

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <Card className="max-w-lg w-full shadow-xl border-0">
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center" style={{backgroundColor: 'rgba(140, 184, 159, 0.15)'}}>
              <CheckCircle className="h-10 w-10" style={{color: '#8CB89F'}} />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Thank You!</h1>
            <p className="text-gray-600 mb-6">
              Your home visit request has been submitted successfully. One of our team will be in touch within 24 hours to confirm your appointment.
            </p>
            <div className="space-y-3">
              <Link to="/get-quote">
                <Button className="w-full" style={{backgroundColor: '#8CB89F'}}>
                  Back to Landing Page
                </Button>
              </Link>
              <Link to="/">
                <Button variant="outline" className="w-full">
                  Visit Our Website
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-100 py-4">
        <div className="max-w-7xl mx-auto px-4 flex justify-between items-center">
          <Link to="/get-quote" className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
            <ArrowLeft className="h-5 w-5" />
            <span>Back</span>
          </Link>
          <img 
            src="https://customer-assets.emergentagent.com/job_window-quotes-1/artifacts/kzwoymfj_Aston%20Lily%20Shutters%20Web%20Banner%20.png" 
            alt="Aston Lily Shutters" 
            className="h-12 md:h-16"
          />
          <div className="hidden md:flex items-center gap-4">
            <a href="tel:01275314990" className="flex items-center gap-2 text-gray-700">
              <Phone className="h-4 w-4" style={{color: '#8CB89F'}} />
              <span className="font-semibold">01275 314990</span>
            </a>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-12">
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Left - Info */}
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Book Your Free Home Visit
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              Our expert surveyor will visit your home to take professional measurements and provide a no-obligation quote tailored to your needs.
            </p>

            {windowCount && windowCount !== 'Not specified' && (
              <div className="mb-8 p-4 rounded-xl" style={{backgroundColor: 'rgba(140, 184, 159, 0.1)'}}>
                <p className="text-gray-700">
                  <span className="font-semibold">Windows to quote:</span> {windowCount}
                </p>
              </div>
            )}

            <div className="space-y-4 mb-8">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{backgroundColor: 'rgba(140, 184, 159, 0.15)'}}>
                    <benefit.icon className="h-6 w-6" style={{color: '#8CB89F'}} />
                  </div>
                  <span className="text-gray-700 font-medium">{benefit.text}</span>
                </div>
              ))}
            </div>

            <Card className="border-0 shadow-lg">
              <CardContent className="p-6">
                <h3 className="font-semibold text-gray-900 mb-4">What happens next?</h3>
                <ol className="space-y-3 text-gray-600">
                  <li className="flex gap-3">
                    <span className="font-bold" style={{color: '#8CB89F'}}>1.</span>
                    We'll call you within 24 hours to confirm a convenient date and time
                  </li>
                  <li className="flex gap-3">
                    <span className="font-bold" style={{color: '#8CB89F'}}>2.</span>
                    Our surveyor will visit to measure and discuss your requirements
                  </li>
                  <li className="flex gap-3">
                    <span className="font-bold" style={{color: '#8CB89F'}}>3.</span>
                    You'll receive a detailed quote with no obligation to proceed
                  </li>
                </ol>
              </CardContent>
            </Card>
          </div>

          {/* Right - Form */}
          <div>
            <Card className="shadow-xl border-0">
              <div className="p-1" style={{backgroundColor: '#8CB89F'}}>
                <p className="text-white text-center text-sm font-medium py-2">
                  Free No-Obligation Home Survey
                </p>
              </div>
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="John Smith"
                      className="mt-1"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="john@example.com"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone">Phone *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        placeholder="07123 456789"
                        className="mt-1"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="postcode">Postcode *</Label>
                    <Input
                      id="postcode"
                      name="postcode"
                      value={formData.postcode}
                      onChange={handleChange}
                      required
                      placeholder="BS1 1AA"
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="address">Full Address</Label>
                    <Textarea
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      placeholder="123 High Street, Bristol"
                      className="mt-1"
                      rows={2}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="preferredDate">Preferred Date</Label>
                      <Input
                        id="preferredDate"
                        name="preferredDate"
                        type="date"
                        value={formData.preferredDate}
                        onChange={handleChange}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="preferredTime">Preferred Time</Label>
                      <select
                        id="preferredTime"
                        name="preferredTime"
                        value={formData.preferredTime}
                        onChange={handleChange}
                        className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                      >
                        <option value="">Select time...</option>
                        <option value="morning">Morning (9am - 12pm)</option>
                        <option value="afternoon">Afternoon (12pm - 5pm)</option>
                        <option value="evening">Evening (5pm - 7pm)</option>
                        <option value="flexible">I'm flexible</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="notes">Additional Notes</Label>
                    <Textarea
                      id="notes"
                      name="notes"
                      value={formData.notes}
                      onChange={handleChange}
                      placeholder="Any specific requirements or questions?"
                      className="mt-1"
                      rows={3}
                    />
                  </div>

                  <Button 
                    type="submit" 
                    size="lg"
                    className="w-full text-white text-lg py-6"
                    style={{backgroundColor: '#8CB89F'}}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Submitting...' : 'Request Home Visit'}
                  </Button>

                  <p className="text-center text-sm text-gray-500">
                    We'll never share your details with third parties
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-gray-900 text-white py-6 mt-12">
        <div className="max-w-7xl mx-auto px-4 flex flex-col md:flex-row justify-center items-center gap-6">
          <a href="tel:01275314990" className="flex items-center gap-2 hover:opacity-80">
            <Phone className="h-5 w-5" />
            <span className="font-semibold">01275 314990</span>
          </a>
          <a href="mailto:orders@astonlily.co.uk" className="flex items-center gap-2 hover:opacity-80">
            <Mail className="h-5 w-5" />
            <span>orders@astonlily.co.uk</span>
          </a>
        </div>
      </div>
    </div>
  );
};

export default BookHomeVisitPage;
